import { useState } from 'react';
import { format } from 'date-fns';
import CurrencyDisplay from '@/components/common/CurrencyDisplay';
import { RiFilter3Fill } from 'react-icons/ri';
import Button, { ButtonSizes, ButtonVariants } from '@/components/Button/Button';
import MTTFiltersForm from '@/pages/tournaments/Tournaments/components/MTTFiltersForm';
import { ColumnDef, SortingState } from '@tanstack/react-table';
import {
  useReactTable,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  flexRender,
} from '@tanstack/react-table';
import { Card, CardBody } from 'reactstrap';
import { useGetTournamentsWithFilters } from '@/redux/api/tournaments';
import TournamentStatusBadge from '@/pages/tournaments/components/TournamentStatus';
import TournamentQuickActions from '../../components/TournamentQuickActions';
import { TournamentStatus } from '@repo/common-types';
import ROUTES from '@/routes/routes-enums';
import Paginator from '@/pages/common/paginator';
import DebouncedInput from '@/components/DebounceInput';
import { useSelector, useDispatch } from 'react-redux';
import { AppState } from '@/redux/root';
import {
  setSkip,
  setTake,
  setSorting,
  setIncludeArchived,
  updateFilter,
  clearTournamentSearchFilters,
  setFilters,
  TournamentFiltersState,
} from '@/redux/slices/tournamentFiltersSlice';

const TournamentsTable = () => {
  const dispatch = useDispatch();
  const { skip, take, filters, sortBy, sortOrder } = useSelector((state: AppState) => state.tournamentFilters);

  const [showFilterModal, setShowFilterModal] = useState(false);

  // Create sorting state from Redux
  const sorting: SortingState = sortBy ? [{ id: sortBy, desc: sortOrder === 'desc' }] : [];

  const { data: tournamentsResponse, isLoading } = useGetTournamentsWithFilters({ sng: false });

  // Filter modal is now handled through Redux slice

  const handleNameSearch = (value: string) => {
    dispatch(updateFilter({ key: 'name', value: value || undefined }));
  };

  const handleSortingChange = (updater: SortingState | ((old: SortingState) => SortingState)) => {
    const newSorting = typeof updater === 'function' ? updater(sorting) : updater;
    if (newSorting.length > 0) {
      dispatch(setSorting({ sortBy: newSorting[0].id, sortOrder: newSorting[0].desc ? 'desc' : 'asc' }));
    } else {
      dispatch(setSorting({ sortBy: undefined, sortOrder: undefined }));
    }
  };

  const columns = [
    {
      header: 'Name',
      accessorKey: 'name',
      size: 600,
      filterFn: 'includesString',
      enableSorting: true,
      cell: ({ row }) => (
        <a href={`${ROUTES.TOURNAMENT_DETAILS}/${row.original.id}`} target="_blank" rel="noopener noreferrer">
          {row.original.name}
        </a>
      ),
    },
    {
      header: 'Status',
      accessorKey: 'status',
      size: 200,
      filterFn: 'inArray',
      enableSorting: true,
      sortingFn: (rowA, rowB) => {
        const statusMap = {
          [TournamentStatus.RUNNING]: 0,
          [TournamentStatus.COMPLETED]: 1,
          [TournamentStatus.CANCELLED]: 2,
          [TournamentStatus.BREAK]: 3,
          [TournamentStatus.PAUSED]: 4,
          [TournamentStatus.CREATED]: 5,
        };

        return statusMap[rowA.original.status] - statusMap[rowB.original.status];
      },
      cell: ({ row }) => {
        return (
          <>
            <TournamentStatusBadge status={row.original.status} deletedAt={row.original.deletedAt} />
            {row.original.status === TournamentStatus.CANCELLED && row.original.cancellationReason && (
              <span className="text-muted ms-1" title={row.original.cancellationReason}>
                {row.original.cancellationReason.length > 20
                  ? `${row.original.cancellationReason.substring(0, 20)}...`
                  : row.original.cancellationReason}
              </span>
            )}
          </>
        );
      },
    },
    {
      header: 'Table Size',
      accessorKey: 'tableSize',
      size: 120,
      cell: ({ row }) => <div className="text-center">{row.original.tableSize}</div>,
      enableSorting: true,
    },
    {
      header: 'Date & Time',
      accessorKey: 'startTime',
      size: 200,
      enableSorting: true,
      cell: ({ row }) => {
        const timezone = Intl.DateTimeFormat('en-us', { timeZoneName: 'short' })
          .formatToParts()
          .find(({ type }) => type === 'timeZoneName')?.value;
        return (
          <div className="d-flex flex-column align-items-start gap-1">
            <span className="text-muted">
              {format(new Date(row.original.startTime), 'MMM dd, HH:mm') + ' ' + timezone}
            </span>
          </div>
        );
      },
      filterFn: 'inDateRange',
      meta: {
        filterVariant: 'range',
      },
    },
    {
      header: 'Game',
      accessorKey: 'game',
      size: 200,
      filterFn: 'includesString',
      enableSorting: true,
    },
    {
      header: 'Buy in',
      accessorKey: 'buyIn',
      size: 200,
      enableSorting: true,
      cell: ({ row }) => {
        const prizePool = row.original.buyIn || 0;
        const rake = row.original.rake || 0;
        const total = prizePool + rake;

        if (rake > 0) {
          return (
            <div className="d-flex flex-column">
              <CurrencyDisplay amount={total} />
              <small className="text-muted">
                ${prizePool.toFixed(2)} + ${rake.toFixed(2)}
              </small>
            </div>
          );
        }
        return <CurrencyDisplay amount={total} />;
      },
      filterFn: 'inRange',
      meta: {
        filterVariant: 'range',
      },
    },
    {
      header: 'GTD',
      accessorKey: 'guaranteedPrizePool',
      size: 200,
      enableSorting: true,
      cell: ({ row }) => {
        return <CurrencyDisplay amount={row.original.guaranteedPrizePool} />;
      },
      filterFn: 'inRange',
      meta: {
        filterVariant: 'range',
      },
    },
    {
      id: 'actions',
      cell: ({ row }: { row: any }) => {
        return <TournamentQuickActions tournament={row.original} />;
      },
    },
  ] as ColumnDef<any>[];

  // Filters are now managed through Redux slice

  const table = useReactTable({
    data: tournamentsResponse?.data ?? [],
    columns,
    state: {
      sorting,
    },
    onSortingChange: handleSortingChange,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    manualPagination: true,
    manualFiltering: true,
    manualSorting: true,
    enableMultiSort: false,
  });

  return (
    <div className="d-flex flex-column w-100 gap-3 py-2">
      <div className="w-100 d-flex justify-content-between align-items-center gap-2 py-2">
        <div className="d-flex align-items-center justify-content-start gap-2">
          <DebouncedInput
            className="form-control w-auto"
            placeholder="Search"
            isLoading={false}
            onChange={(val) => handleNameSearch(val as string)}
            value={filters?.name || ''}
          />
          <Button
            onClick={() => setShowFilterModal(true)}
            className="btn btn-outline d-flex align-items-center justify-content-center gap-2"
            variant={ButtonVariants.secondary}
            size={ButtonSizes.sm}
          >
            <RiFilter3Fill size={20} />
            Filter
          </Button>
          {(filters?.game?.length ||
            filters?.status?.length ||
            filters?.startTime ||
            filters?.buyIn ||
            filters?.guaranteedPrizePool ||
            filters?.name) && (
            <Button
              size={ButtonSizes.sm}
              variant={ButtonVariants.primary}
              onClick={() => {
                dispatch(clearTournamentSearchFilters());
              }}
              className="btn btn-primary d-flex align-items-center justify-content-center gap-2"
            >
              Clear Filters
            </Button>
          )}
        </div>
      </div>
      <Card className="w-auto">
        <CardBody className="w-100">
          {isLoading ? (
            <div className="p-5 text-center">
              <div className="spinner-border" role="status">
                <span className="sr-only">Loading...</span>
              </div>
            </div>
          ) : (
            <table className="table-hover table-centered table-nowrap mb-0 table align-middle">
              <thead>
                {table.getHeaderGroups().map((headerGroup) => (
                  <tr key={headerGroup.id}>
                    {headerGroup.headers.map((header) => (
                      <th
                        key={header.id}
                        onClick={header.column.getToggleSortingHandler()}
                        className={header.column.getCanSort() ? 'cursor-pointer' : ''}
                      >
                        {flexRender(header.column.columnDef.header, header.getContext())}
                        {header.column.getCanSort() ? (
                          header.column.getIsSorted() ? (
                            header.column.getIsSorted() === 'desc' ? (
                              <i className="ri-arrow-down-s-line" />
                            ) : (
                              <i className="ri-arrow-up-s-line" />
                            )
                          ) : (
                            <i className="ri-arrow-up-down-line" />
                          )
                        ) : null}
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
              <tbody>
                {table.getRowModel().rows.map((row) => (
                  <tr key={row.id}>
                    {row.getVisibleCells().map((cell) => (
                      <td key={cell.id}>{flexRender(cell.column.columnDef.cell, cell.getContext())}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardBody>
      </Card>
      <Paginator
        take={take}
        skip={skip}
        showing={tournamentsResponse?.data?.length || 0}
        totalCount={tournamentsResponse?.total || 0}
        setTake={(value) => dispatch(setTake(value))}
        setSkip={(value) => dispatch(setSkip(value))}
      />
      {showFilterModal && <MTTFiltersForm onClose={() => setShowFilterModal(false)} />}
    </div>
  );
};

export default TournamentsTable;
